import React from 'react';
import { Mail, Phone, MapPin, Github, Linkedin, Twitter, ExternalLink, Download } from 'lucide-react';
import { profileData } from '../data/mockData';
import { Button } from './ui/button';

const Contact = () => {
  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: profileData.email,
      href: `mailto:${profileData.email}`,
      color: 'bg-red-100 text-red-600'
    },
    {
      icon: Phone,
      label: 'Phone',
      value: profileData.phone,
      href: `tel:${profileData.phone}`,
      color: 'bg-emerald-100 text-emerald-600'
    },
    {
      icon: MapPin,
      label: 'Location',
      value: profileData.location,
      href: null,
      color: 'bg-blue-100 text-blue-600'
    }
  ];

  const socialLinks = [
    { 
      icon: Github, 
      href: profileData.socialLinks.github, 
      label: 'GitHub',
      username: '@kailashkumar210',
      color: 'hover:bg-gray-900 hover:text-white'
    },
    { 
      icon: Linkedin, 
      href: profileData.socialLinks.linkedin, 
      label: 'LinkedIn',
      username: 'Kailash Kumar',
      color: 'hover:bg-blue-600 hover:text-white'
    },
    { 
      icon: Twitter, 
      href: profileData.socialLinks.twitter, 
      label: 'Twitter / X',
      username: '@kailashkumar210',
      color: 'hover:bg-gray-900 hover:text-white'
    }
  ];

  return (
    <section id="contact" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-emerald-600 font-medium text-sm tracking-wide uppercase">
            Get In Touch
          </span>
          <h2 className="text-3xl sm:text-4xl font-light text-gray-900 mt-2">
            Let's{' '}
            <span className="font-semibold">Connect</span>
          </h2>
          <p className="text-gray-500 mt-4 max-w-xl mx-auto">
            Have a question or want to work together? Feel free to reach out through 
            any of the channels below.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Contact Cards Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {contactInfo.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 group"
              >
                <div className={`w-12 h-12 rounded-xl ${item.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-6 h-6" />
                </div>
                <p className="text-sm text-gray-500 mb-1">{item.label}</p>
                {item.href ? (
                  <a
                    href={item.href}
                    className="text-gray-900 font-semibold hover:text-emerald-600 transition-colors text-lg"
                  >
                    {item.value}
                  </a>
                ) : (
                  <p className="text-gray-900 font-semibold text-lg">{item.value}</p>
                )}
              </div>
            ))}
          </div>

          {/* Social Links */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">
              Connect With Me
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center space-x-4 p-4 rounded-xl bg-gray-50 border border-gray-100 transition-all duration-300 group ${social.color}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center group-hover:shadow-md transition-shadow">
                    <social.icon className="w-6 h-6 text-gray-700 group-hover:text-current transition-colors" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 group-hover:text-current transition-colors">
                      {social.label}
                    </p>
                    <p className="text-sm text-gray-500 group-hover:text-current/80 transition-colors">
                      {social.username}
                    </p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-current transition-colors" />
                </a>
              ))}
            </div>
          </div>

          {/* Download CV CTA */}
          <div className="mt-12 text-center">
            <div className="inline-flex flex-col items-center p-8 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl text-white">
              <h3 className="text-xl font-semibold mb-2">Want to know more?</h3>
              <p className="text-gray-300 mb-6 text-sm">Download my complete resume for detailed information</p>
              <a href={profileData.cvUrl} download="Kailash_Kumar_CV.pdf">
                <Button
                  size="lg"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-base font-medium transition-all duration-300 hover:scale-105"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Resume
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
